import { computed, type Ref } from 'vue';
import { isAxiosError } from 'axios';
import { useModal } from 'vue-final-modal';

import { ConfirmDeleteModal } from '@/shared/ui';
import { useCustomToast } from '@/shared/lib';
import { createDeleteResultMessages, type BulkDeleteResult } from '../utils/documentDeleteMessages';

interface UseDocumentDeleteOptions {
  selectedDocuments: Ref<number[]>;
  deselectAllDocuments: () => void;
  onDelete?: ((deletedIds: number[]) => void) | ((id: number) => void);
  onDeletePerId?: boolean;
  deleteDocuments: (ids: number[]) => Promise<{
    succeededIds: number[];
    successCount: number;
    invalidIds: any[];
    failedOperations: Array<{ id: number; error: string }>;
    fileOperationsFailed: boolean;
    warnings: string[];
  }>;
  onAfterDelete?: () => Promise<void> | void;
  resetSelection?: () => void;
  entityLabel?: string;
}

export function useDocumentDelete(options: UseDocumentDeleteOptions) {
  const {
    selectedDocuments,
    deselectAllDocuments,
    onDelete,
    onDeletePerId = false,
    deleteDocuments,
    onAfterDelete,
    resetSelection,
    entityLabel = 'documento',
  } = options;

  const toast = useCustomToast();

  const showToastMessages = (messages: ReturnType<typeof createDeleteResultMessages>) => {
    messages.forEach((msg) => {
      switch (msg.type) {
        case 'success':
          toast.showSuccessToast(msg);
          break;
        case 'warning':
          toast.showWarningToast(msg);
          break;
        case 'error':
          toast.showErrorToast(msg);
          break;
      }
    });
  };

  const handleDelete = async () => {
    closeDeleteConfirmation();

    if (!selectedDocuments.value || selectedDocuments.value.length === 0) {
      toast.showErrorToast({
        title: `Nenhum ${entityLabel} selecionado`,
        message: `Selecione pelo menos um ${entityLabel} para deletar.`,
        type: 'error',
      });
      return;
    }

    try {
      const result = await deleteDocuments(selectedDocuments.value);

      // Handle onDelete callback
      if (onDelete && result.succeededIds && result.succeededIds.length > 0) {
        if (onDeletePerId) {
          // Call onDelete for each ID individually
          result.succeededIds.forEach((id) => {
            (onDelete as (id: number) => void)(id);
          });
        } else {
          // Call onDelete with array of IDs
          (onDelete as (deletedIds: number[]) => void)(result.succeededIds);
        }
      }

      const messages = createDeleteResultMessages(result as BulkDeleteResult);
      showToastMessages(messages);

      // Call optional post-delete callback (e.g., for fetching notification counts)
      if (onAfterDelete) {
        await onAfterDelete();
      }

      deselectAllDocuments();

      return result.succeededIds;
    } catch (error: unknown) {
      const selectedCount = selectedDocuments.value?.length ?? 0;
      const errorMessage =
        isAxiosError(error) && typeof error.response?.data?.message === 'string'
          ? error.response.data.message
          : 'Tente novamente';
      toast.showErrorToast({
        title: `Erro ao deletar ${entityLabel}${selectedCount > 1 ? 's' : ''}`,
        message: errorMessage,
        type: 'error',
      });
      resetSelection?.();
    }
  };

  const modalTitle = computed(() => {
    const count = selectedDocuments.value?.length ?? 0;
    if (count <= 1) {
      return `Tem certeza que deseja deletar o ${entityLabel}?`;
    }
    return `Tem certeza que deseja deletar ${count} ${entityLabel}s?`;
  });

  const { open: openDeleteConfirmation, close: closeDeleteConfirmation } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      get title() {
        return modalTitle.value;
      },
      description: 'A ação não pode ser desfeita.',
      onClose: async () => {
        closeDeleteConfirmation();
        // Call optional post-delete callback (e.g., for fetching notification counts)
        if (onAfterDelete) {
          await onAfterDelete();
        }
      },
      onConfirm: () => handleDelete(),
    },
  });

  const handleOpenDeleteConfirmation = () => {
    if (!selectedDocuments.value || selectedDocuments.value.length === 0) {
      toast.showErrorToast({
        title: `Nenhum ${entityLabel} selecionado`,
        message: `Selecione pelo menos um ${entityLabel} para deletar.`,
        type: 'error',
      });
      return;
    }

    openDeleteConfirmation();
  };

  return {
    handleDelete,
    handleOpenDeleteConfirmation,
    openDeleteConfirmation,
    closeDeleteConfirmation,
  };
}
